import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { fetchPreReleases, createPreRelease, approvePreRelease, rejectPreRelease } from "../services/preReleaseServices";
import { fetchBranches } from "../services/branchApi";
import { fetchUsers } from "../services/usersApi";
import PreReleaseTable from "../components/admin/preRelease/PreReleaseTable";
import Pagination from "../components/CommonComponents/Pagination";
import SearchInput from "../components/CommonComponents/SearchInput";
import PreReleaseDetailsModal from "../components/admin/preRelease/PreReleaseDetailsModal";
import EmptyState from "../components/CommonComponents/EmptyState";
import FilterBar from "../components/CommonComponents/FilterBar";
import AlertModal from "../components/CommonComponents/AlertModal";
import ConfirmationModal from "../components/CommonComponents/ConfirmationModal";
import AdminPreReleaseModal from "../components/admin/preRelease/AdminPreReleaseModal";
import { Package, Plus } from 'lucide-react';

const PreReleaseTracker = () => {
    const [searchParams] = useSearchParams();
    const user = useSelector(state => state.auth.user);
    const customer_id = user?.customer_id;
    const currentUserOption = user?.id
        ? {
            value: String(user.id),
            label: user.username || user.email || "Current admin"
        }
        : null;

    const [preReleases, setPreReleases] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(null); // id of pre-release being acted on
    const [createLoading, setCreateLoading] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [selected, setSelected] = useState(null); // for details modal

    // Confirmation modal state for approve/reject
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        action: null, // 'approve' | 'reject'
        preRelease: null,
    });

    // Alert modal state
    const [alertModal, setAlertModal] = useState({
        isOpen: false,
        type: 'info',
        title: '',
        message: ''
    });

    // Filter states
    const [statusFilter, setStatusFilter] = useState("");
    const [branchFilter, setBranchFilter] = useState("");
    const [requesterFilter, setRequesterFilter] = useState("");
    const [dateFilter, setDateFilter] = useState("month");
    const [customStartDate, setCustomStartDate] = useState("");
    const [customEndDate, setCustomEndDate] = useState("");

    // Data for filters
    const [branches, setBranches] = useState([]);
    const [users, setUsers] = useState([]);
    const activeBranches = branches.filter(branch => String(branch.status).toUpperCase() === "ACTIVE");

    const pageSize = 5;

    // Fetch branches and users for filters
    useEffect(() => {
        if (!customer_id) return;

        // Fetch branches
        fetchBranches(customer_id)
            .then(res => {
                setBranches(res.data.data || []);
            })
            .catch(() => {
                setBranches([]);
            });

        // Fetch users for requester filter
        fetchUsers()
            .then(res => {
                setUsers(res.data.data || []);
            })
            .catch(() => {
                setUsers([]);
            });
    }, [customer_id]);

    const refreshData = useCallback(async (page = 1) => {
        if (dateFilter === "custom" && (!customStartDate || !customEndDate)) {
            setPreReleases([]);
            setTotalPages(1);
            setLoading(false);
            setError(null);
            return;
        }

        setLoading(true);
        setError(null);
        try {
            // Build filters object
            const filters = {};
            if (statusFilter) filters.status = statusFilter;
            if (branchFilter) filters.branch_id = Number(branchFilter);
            if (requesterFilter) filters.user_id = Number(requesterFilter);
            if (dateFilter) {
                filters.dateFilter = dateFilter;
                if (dateFilter === "custom") {
                    if (customStartDate) filters.startDate = customStartDate;
                    if (customEndDate) filters.endDate = customEndDate;
                }
            }

            // fetchPreReleases returns { data, totalPages, ... }
            const res = await fetchPreReleases(page, pageSize, filters);
            setPreReleases(res.data.data || []);
            setTotalPages(res.data.totalPages || 1);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [pageSize, statusFilter, branchFilter, requesterFilter, dateFilter, customStartDate, customEndDate]);

    const handleApprove = async (id) => {
        setActionLoading(id);
        try {
            await approvePreRelease(id);
            await refreshData(currentPage);
            setAlertModal({
                isOpen: true,
                type: 'success',
                title: 'Success',
                message: 'Pre-release approved successfully!'
            });
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || "Failed to approve";
            setAlertModal({
                isOpen: true,
                type: 'error',
                title: 'Approval Failed',
                message: errorMessage
            });
        } finally {
            setActionLoading(null);
        }
    };

    const handleReject = async (id) => {
        setActionLoading(id);
        try {
            await rejectPreRelease(id);
            await refreshData(currentPage);
            setAlertModal({
                isOpen: true,
                type: 'success',
                title: 'Success',
                message: 'Pre-release rejected successfully!'
            });
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || "Failed to reject";
            setAlertModal({
                isOpen: true,
                type: 'error',
                title: 'Rejection Failed',
                message: errorMessage
            });
        } finally {
            setActionLoading(null);
        }
    };

    const handleCreatePreRelease = async (formData, approve) => {
        setCreateLoading(true);
        try {
            await createPreRelease(formData);
            setCreateModalOpen(false);
            await refreshData(1);
            setCurrentPage(1);
            setAlertModal({
                isOpen: true,
                type: 'success',
                title: 'Success',
                message: approve ? 'Pre-release submitted and approved successfully!' : 'Pre-release submitted successfully!'
            });
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || "Failed to submit pre-release.";
            setAlertModal({
                isOpen: true,
                type: 'error',
                title: 'Submission Failed',
                message: errorMessage
            });
            throw err;
        } finally {
            setCreateLoading(false);
        }
    };

    const openConfirmModal = (action, preRelease) => {
        setConfirmModal({
            isOpen: true,
            action,
            preRelease,
        });
    };

    const closeConfirmModal = () => {
        setConfirmModal({ isOpen: false, action: null, preRelease: null });
    };

    const handleConfirmAction = async () => {
        if (!confirmModal.preRelease) return;
        const { action, preRelease } = confirmModal;
        closeConfirmModal();
        if (action === 'approve') {
            await handleApprove(preRelease.id);
        } else if (action === 'reject') {
            await handleReject(preRelease.id);
        }
    };

    useEffect(() => {
        refreshData(currentPage);
        // eslint-disable-next-line
    }, [currentPage, statusFilter, branchFilter, requesterFilter, dateFilter, customStartDate, customEndDate]);

    useEffect(() => {
        if (dateFilter !== "custom") {
            setCustomStartDate("");
            setCustomEndDate("");
        }
    }, [dateFilter]);

    // ✅ AUTO-SELECT from notification: Watch for 'focus' query param
    useEffect(() => {
        const focusId = searchParams.get('focus');
        if (focusId && preReleases.length > 0) {
            const focusedItem = preReleases.find(pr => pr.id === parseInt(focusId));
            if (focusedItem) {
                setSelected(focusedItem);
                console.log(`✅ Auto-selected Pre-Release ${focusId} from notification`);
            }
        }
    }, [searchParams, preReleases]);

    // Filter data by search
    const filteredPreReleases = search.trim()
        ? preReleases.filter(pr => {
            const term = search.trim().toLowerCase();
            return (
                pr.remarks?.toLowerCase().includes(term) ||
                pr.user?.username?.toLowerCase().includes(term) ||
                pr.user?.email?.toLowerCase().includes(term) ||
                pr.branch?.name?.toLowerCase().includes(term) ||
                pr.status?.toLowerCase().includes(term)
            );
        })
        : preReleases;

    // Calculate item range for summary
    const totalItems = filteredPreReleases.length < pageSize && totalPages === 1 ? filteredPreReleases.length : pageSize * totalPages;
    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(startItem + filteredPreReleases.length - 1, totalItems);

    // Prepare filter configurations for FilterBar
    const filterConfigs = [
        {
            type: 'react-select',
            label: 'Branch',
            value: (() => {
                if (!branchFilter) return { value: "", label: "All Branches" };
                const found = activeBranches.find(b => String(b.id) === branchFilter);
                return found ? { value: String(found.id), label: found.name } : { value: branchFilter, label: branchFilter };
            })(),
            onChange: (value) => {
                setBranchFilter(value);
                setCurrentPage(1);
            },
            options: [
                { value: "", label: "All Branches" },
                ...(activeBranches.map(branch => ({ value: String(branch.id), label: branch.name })))
            ],
            isSearchable: true,
            placeholder: "All Branches",
            minWidth: '180px',
        },
        {
            type: 'react-select',
            label: 'Date',
            value: (() => {
                const labelMap = {
                    all: "All",
                    today: "Today",
                    week: "This Week",
                    month: "This Month",
                    custom: "Custom",
                };
                const value = dateFilter || "month";
                return { value, label: labelMap[value] || value };
            })(),
            onChange: (value) => {
                setDateFilter(value);
                setCurrentPage(1);
            },
            options: [
                { value: "all", label: "All" },
                { value: "today", label: "Today" },
                { value: "week", label: "This Week" },
                { value: "month", label: "This Month" },
                { value: "custom", label: "Custom" },
            ],
            isSearchable: true,
            placeholder: "This Month",
            minWidth: '150px',
        },
        {
            type: 'date',
            label: 'Start Date',
            value: customStartDate,
            onChange: (value) => {
                setCustomStartDate(value);
                setCurrentPage(1);
            },
            minWidth: '170px',
            hide: dateFilter !== "custom",
            max: customEndDate || undefined,
        },
        {
            type: 'date',
            label: 'End Date',
            value: customEndDate,
            onChange: (value) => {
                setCustomEndDate(value);
                setCurrentPage(1);
            },
            minWidth: '170px',
            hide: dateFilter !== "custom",
            min: customStartDate || undefined,
        },
        {
            type: 'react-select',
            label: 'Status',
            value: (() => {
                if (!statusFilter) return { value: "", label: "All Status" };
                const labelMap = {
                    PENDING: "Pending",
                    APPROVED: "Approved",
                    REJECTED: "Rejected",
                };
                return { value: statusFilter, label: labelMap[statusFilter] || statusFilter };
            })(),
            onChange: (value) => {
                setStatusFilter(value);
                setCurrentPage(1);
            },
            options: [
                { value: "", label: "All Status" },
                { value: "PENDING", label: "Pending" },
                { value: "APPROVED", label: "Approved" },
                { value: "REJECTED", label: "Rejected" },
            ],
            isSearchable: true,
            placeholder: "All Status",
            minWidth: '140px',
        },
        {
            type: 'react-select',
            label: 'Requested by',
            value: (() => {
                if (!requesterFilter) return { value: "", label: "All Requesters" };
                const found = users.find(u => String(u.id) === requesterFilter)
                    || (currentUserOption?.value === requesterFilter ? currentUserOption : null);
                return found ? { value: String(found.id || found.value), label: found.username || found.email || found.label } : { value: requesterFilter, label: requesterFilter };
            })(),
            onChange: (value) => {
                setRequesterFilter(value);
                setCurrentPage(1);
            },
            options: [
                { value: "", label: "All Requesters" },
                ...(currentUserOption ? [currentUserOption] : []),
                ...(users.map(user => ({
                    value: String(user.id),
                    label: user.username || user.email
                })))
            ],
            isSearchable: true,
            placeholder: "All Requesters",
            minWidth: '180px',
        },
    ];

    return (
        <div className="pt-6 flex flex-col gap-6">
            {/* Search Bar */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <SearchInput
                    value={search}
                    onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
                    placeholder="Search by remarks, requester, branch, or status..."
                    disabled={loading}
                />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text)' }}>Pre-Release Tracker</h1>
                <button
                    type="button"
                    onClick={() => setCreateModalOpen(true)}
                    className="inline-flex items-center gap-2 rounded-md bg-[#062b4f] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#0a3b68] disabled:opacity-60"
                    disabled={loading && activeBranches.length === 0}
                >
                    <Plus size={18} />
                    Add Pre-Release
                </button>
            </div>

            {/* Filters - Apple-inspired minimalist design */}
            <FilterBar filters={filterConfigs} />

            {error ? (
                <EmptyState
                    icon={Package}
                    title="Failed to Load Pre-Releases"
                    message="Check your internet connection and try again."
                    onRetry={() => refreshData(currentPage)}
                    retryLabel="Retry"
                />
            ) : loading ? (
                <div className="text-center py-10 text-gray-500">Loading pre-releases...</div>
            ) : filteredPreReleases.length === 0 ? (
                <EmptyState
                    icon={Package}
                    title="No Pre-Releases Found"
                    message={search.trim() ? "No pre-releases match your search." : "No pre-release requests yet."}
                />
            ) : (
                <>
                    <PreReleaseTable
                        data={filteredPreReleases}
                        loading={loading}
                        actionLoading={actionLoading}
                        onApprove={(pr) => openConfirmModal('approve', pr)}
                        onReject={(pr) => openConfirmModal('reject', pr)}
                        onViewDetails={setSelected}
                    />
                    <div className="flex flex-col items-center gap-1">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />
                        <div className="text-sm text-gray-500 text-center">
                            {`Showing ${startItem} – ${endItem} of ${totalItems} items | Page ${currentPage} of ${totalPages}`}
                        </div>
                    </div>
                </>
            )}
            <PreReleaseDetailsModal preRelease={selected} onClose={() => setSelected(null)} />

            <AdminPreReleaseModal
                isOpen={createModalOpen}
                onClose={() => !createLoading && setCreateModalOpen(false)}
                onSubmit={handleCreatePreRelease}
                branches={activeBranches}
                loading={createLoading}
            />

            {/* Alert Modal for errors and success messages */}
            <AlertModal
                isOpen={alertModal.isOpen}
                onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
                type={alertModal.type}
                title={alertModal.title}
                message={alertModal.message}
            />

            {/* Confirmation Modal for approve/reject */}
            <ConfirmationModal
                isOpen={confirmModal.isOpen}
                onClose={closeConfirmModal}
                onConfirm={handleConfirmAction}
                title={confirmModal.action === 'approve' ? 'Approve Pre-Release' : 'Reject Pre-Release'}
                message={(() => {
                    const pr = confirmModal.preRelease;
                    if (!pr) return '';
                    const branchName = pr.branch?.name || pr.branch_name || 'Unknown branch';
                    const requester = pr.user?.username || pr.user?.email || `User #${pr.user_id || '-'}`;
                    return `Are you sure you want to ${confirmModal.action} the pre-release from ${requester} (${branchName})?`;
                })()}
                confirmText={confirmModal.action === 'approve' ? 'Approve' : 'Reject'}
                cancelText="Cancel"
                type={confirmModal.action === 'reject' ? 'delete' : 'default'}
            />
        </div>
    );
};

export default PreReleaseTracker;
